import {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import { loginApi, getSessionApi, logoutApi } from '@services/auth';
import { StatusCodes } from 'http-status-codes';
import WidgetsLoader from '@components/WidgetsLoader';
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext(null);

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const setAuthToken = (accessToken) => {
  window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
};

export const setRefreshToken = (accessToken) => {
  window.localStorage.setItem(REFRESH_TOKEN_KEY, accessToken);
};

export const getAuthToken = () => {
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = () => {
  return window.localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticationError, setAuthenticationError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const bootstrapAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const { isOk, data } = await getSessionApi();
      if (isOk && data) {
        setUser(data);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (e) {
      console.error('bootstrapAuth.catch', e);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      bootstrapAuth();
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, [bootstrapAuth]);

  const loginWithEmailPassword = useCallback(
    async (email, password, keep_logged_in) => {
      try {
        setAuthenticationError('');
        const { isOk, data, status, error } = await loginApi(
          email,
          password,
          keep_logged_in
        );
        if (
          isOk &&
          data.tokens &&
          data.tokens.access_token &&
          data.tokens.refresh_token
        ) {
          const { access_token, refresh_token } = data.tokens;
          setAuthToken(access_token);
          setRefreshToken(refresh_token);
          await bootstrapAuth();
          navigate('/');
          return { isOk: true };
        }
        if (
          status === StatusCodes.BAD_REQUEST ||
          status === StatusCodes.UNAUTHORIZED
        ) {
          if (error && typeof error === 'object') {
            for (const [, value] of Object.entries(error)) {
              setAuthenticationError(`${value}`);
              return { isOk: false };
            }
          }
          if (typeof error === 'string') {
            setAuthenticationError(error);
            return { isOk: false };
          }
        }
        setAuthenticationError('');
        return { isOk: false };
      } catch (e) {
        setAuthenticationError('Something went wrong');
        return { isOk: false };
      }
    },
    [navigate, bootstrapAuth]
  );

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } finally {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  const values = useMemo(() => {
    return {
      user,
      loginWithEmailPassword,
      logout,
      isAuthenticated,
      isLoading,
      bootstrapAuth,
      setIsAuthenticated,
      authenticationError
    };
  }, [
    loginWithEmailPassword,
    logout,
    isAuthenticated,
    isLoading,
    authenticationError,
    user,
    bootstrapAuth
  ]);

  if (isLoading) {
    return <WidgetsLoader />;
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};
